import { computed, ref } from 'vue'

import type { ComputedRef, Ref } from 'vue'
import type { MultiselectSearchOption, NativeSelectOption } from '@shared/ui/fields'
import type { TutorKanbanFilterParams } from './interfaces'

import { useFilterSearch } from '@shared/lib/filters'

import { buildQueryObject, parseFilterParams } from './filters/parse-filter-params'
import { useFilterCategory } from './filters/use-filter-category.hook'
import { useFilterStrictCategory } from './filters/use-filter-strict-category.hook'
import { useFilterPaidLabel } from './filters/filter-paid-label.hook'
import { useFilterAgents } from './filters/use-filter-agents.hook'
import { useFilterSort } from './filters/use-filter-sort.hook'
import { useFilterStageReason } from './filters/use-filter-stage-reason.hook'

interface TutorKanbanFiltersReturn {
  filterResetKey: Ref<number>
  searchQuery: Ref<string>
  categoryOptions: ComputedRef<MultiselectSearchOption[]>
  categoryInitialValue: ComputedRef<string[]>
  handleCategorySelected: (selected: MultiselectSearchOption[]) => void
  strictCategoryOptions: ComputedRef<NativeSelectOption[]>
  strictCategoryValue: Ref<string | null>
  handleStrictCategoryChange: (option: NativeSelectOption) => void
  paidLabelOptions: ComputedRef<MultiselectSearchOption[]>
  paidLabelInitialValue: ComputedRef<string[]>
  handlePaidLabelSelected: (selected: MultiselectSearchOption[]) => void
  agentsOptions: ComputedRef<MultiselectSearchOption[]>
  agentsInitialValue: ComputedRef<string[]>
  handleAgentsSelected: (selected: MultiselectSearchOption[]) => void
  reasonsOptions: ComputedRef<MultiselectSearchOption[]>
  reasonsInitialValue: ComputedRef<string[]>
  handleReasonsSelected: (selected: MultiselectSearchOption[]) => void
  sortOptions: NativeSelectOption[]
  sortValue: Ref<string | null>
  handleSortChange: (option: NativeSelectOption) => void
  handleSearch: () => void
  handleClear: () => void
}

export const useTutorKanbanFilters = (): TutorKanbanFiltersReturn => {
  const route = useRoute()
  const router = useRouter()

  const filterResetKey = ref(0)

  const initialParams = parseFilterParams(route.query)

  const {
    query: searchQuery,
  } = useFilterSearch({ initial: initialParams.q })

  const {
    options: categoryOptions,
    initialValue: categoryInitialValue,
    handleSelected: handleCategorySelected,
    query: categoryQuery,
  } = useFilterCategory({ initial: initialParams.categories })

  const {
    options: strictCategoryOptions,
    query: strictCategoryValue,
    handleChange: handleStrictCategoryChange,
  } = useFilterStrictCategory({ initial: initialParams.strict_categories })

  const {
    options: paidLabelOptions,
    initialValue: paidLabelInitialValue,
    handleSelected: handlePaidLabelSelected,
    query: paidLabelQuery,
  } = useFilterPaidLabel({ initial: initialParams.paid_label })

  const {
    options: agentsOptions,
    initialValue: agentsInitialValue,
    handleSelected: handleAgentsSelected,
    query: agentsQuery,
  } = useFilterAgents({ initial: initialParams.agents })

  const {
    options: reasonsOptions,
    initialValue: reasonsInitialValue,
    handleSelected: handleReasonsSelected,
    query: reasonsQuery,
  } = useFilterStageReason({ initial: initialParams.reasons })

  const {
    options: sortOptions,
    query: sortValue,
    sortBy,
    sortDirection,
    handleChange: handleSortChange,
  } = useFilterSort({
    initialSortBy: initialParams.sort_by,
    initialSortDirection: initialParams.sort_direction,
  })

  const queryParams = computed<TutorKanbanFilterParams>(() => ({
    q: searchQuery.value,
    categories: categoryQuery.value,
    strict_categories: strictCategoryValue.value,
    paid_label: paidLabelQuery.value,
    agents: agentsQuery.value,
    reasons: reasonsQuery.value,
    sort_by: sortBy.value,
    sort_direction: sortDirection.value,
  }))

  const handleSearch = (): void => {
    const query = buildQueryObject(queryParams.value)

    void router.push({ query })
  }

  const handleClear = (): void => {
    searchQuery.value = ''
    categoryQuery.value = []
    strictCategoryValue.value = null
    paidLabelQuery.value = []
    agentsQuery.value = []
    reasonsQuery.value = []
    sortValue.value = null
    filterResetKey.value++

    void router.push({ query: {} })
  }

  return {
    filterResetKey,
    searchQuery,
    categoryOptions,
    categoryInitialValue,
    handleCategorySelected,
    strictCategoryOptions,
    strictCategoryValue,
    handleStrictCategoryChange,
    paidLabelOptions,
    paidLabelInitialValue,
    handlePaidLabelSelected,
    agentsOptions,
    agentsInitialValue,
    handleAgentsSelected,
    reasonsOptions,
    reasonsInitialValue,
    handleReasonsSelected,
    sortOptions,
    sortValue,
    handleSortChange,
    handleSearch,
    handleClear,
  }
}
